import { apiRequest } from "./apiClient";

export const unwrapData = (response) => response?.data ?? response ?? {};

export const toList = (payload) => {
  const data = unwrapData(payload);

  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.requests)) return data.requests;

  return [];
};

export const buildQuery = (params = {}) => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, value);
    }
  });

  return query.toString();
};

export const listKupatHolimRequests = async (params = {}) => {
  const query = buildQuery(params);
  const response = await apiRequest(
    `/kupat-holim-requests${query ? `?${query}` : ""}`,
  );

  return toList(response);
};

export const listKupatHolims = async () => {
  const response = await apiRequest("/kupat-holims");
  const data = unwrapData(response);
  return data.kupatHolims || response?.kupatHolims || [];
};

const appendKupatHolimSettingsForm = (payload) => {
  const formData = new FormData();

  ["title", "title2English", "title2Hebrew"].forEach((key) => {
    if (payload[key] !== undefined) formData.append(key, payload[key]);
  });
  if (payload.image) formData.append("image", payload.image);

  return formData;
};

export const listKupatHolimSettings = async () => {
  // `/kupat-holims` is the primary authenticated settings-list endpoint.
  // It returns the provider settings in its `kupatHolims` field.
  return listKupatHolims();
};

export const getKupatHolimSettings = async (kupatHolimId) => {
  const response = await apiRequest(
    `/kupat-holims/${encodeURIComponent(kupatHolimId)}/settings`,
  );
  const data = unwrapData(response);
  return data?.setting || response?.setting || data;
};

export const createKupatHolimSettings = (payload) =>
  apiRequest(`/kupat-holims/${encodeURIComponent(payload.kupatHolimId)}/settings`, {
    method: "POST",
    body: appendKupatHolimSettingsForm(payload),
  });

export const updateKupatHolimSettings = (kupatHolimId, payload) =>
  apiRequest(`/kupat-holims/${encodeURIComponent(kupatHolimId)}/settings`, {
    method: "PATCH",
    body: appendKupatHolimSettingsForm(payload),
  });

export const deleteKupatHolimSettings = (kupatHolimId) =>
  apiRequest(`/kupat-holims/${encodeURIComponent(kupatHolimId)}/settings`, {
    method: "DELETE",
  });

export const getKupatHolimRequest = async (id) => {
  const response = await apiRequest(`/kupat-holim-requests/${id}`);
  const data = unwrapData(response);

  return data?.request ?? data?.item ?? data?.data ?? data;
};

export const reviewKupatHolimRequest = async (id, payload) => {
  return apiRequest(`/kupat-holim-requests/${id}/review`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
};

export const invoiceKupatHolimRequest = async (requestId, payload) =>
  apiRequest(`/kupat-holim-requests/${encodeURIComponent(requestId)}/invoice`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

export const listUsers = async (params) => {
  const query = buildQuery(params);
  const response = await apiRequest(`/users${query ? `?${query}` : ""}`);
  const data = unwrapData(response);
  const items = toList(response);
  const pagination = data?.pagination || response?.pagination || {};
  const totalPages = pagination.totalPages ?? data?.totalPages ?? response?.totalPages;
  const total = pagination.total ?? data?.total ?? response?.total;
  const limit = Number(pagination.limit ?? data?.limit ?? response?.limit ?? params?.limit);
  items.totalPages = totalPages != null ? Number(totalPages) : (total != null && limit > 0 ? Math.ceil(Number(total) / limit) : null);
  return items;
};

export const listUserBeneficiaries = async (userId) =>
  toList(await apiRequest(`/users/${encodeURIComponent(userId)}/beneficiaries`));

export const listUserAddresses = async (userId) =>
  toList(await apiRequest(`/users/${encodeURIComponent(userId)}/addresses`));

export const createUser = async (payload) => {
  return apiRequest("/users", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
};

export const updateUser = async (id, payload) => {
  return apiRequest(`/users/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
};

export const deleteUser = async (id) => {
  return apiRequest(`/users/${id}`, { method: "DELETE" });
};

export const listDeliveryDrivers = async () => {
  const response = await apiRequest("/driver-deliveries/drivers");
  return toList(response);
};

export const getOrderDriverLocation = async (orderId) => {
  const response = await apiRequest(`/driver-deliveries/orders/${encodeURIComponent(orderId)}/location`);
  return unwrapData(response);
};

export const getAccountDeletionRequest = async () =>
  unwrapData(await apiRequest("/account/deletion-request", { skipAuthRedirect: true }));

export const requestAccountDeletion = async () =>
  unwrapData(await apiRequest("/account/deletion-request", { method: "POST", skipAuthRedirect: true }));

export const cancelAccountDeletion = async () =>
  unwrapData(await apiRequest("/account/deletion-request", { method: "DELETE", skipAuthRedirect: true }));

const catalogRequest = (path, method = "GET", payload) =>
  apiRequest(path, {
    method,
    ...(payload === undefined
      ? {}
      : {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
  });

const catalogList = async (path) => {
  const response = await catalogRequest(path);
  const data = unwrapData(response);
  const items = toList(data);

  // Keep the established array return type for current consumers (such as billing),
  // while exposing the API's pagination envelope to catalogue workspaces.
  items.pagination = {
    total: Number(data?.total ?? items.length),
    page: Number(data?.page ?? 1),
    limit: Number(data?.limit ?? items.length),
    totalPages: Number(data?.totalPages ?? 1),
  };
  return items;
};

export const listPrescriptionMedications = async (params = {}) => {
  const query = buildQuery(params);
  return catalogList(`/prescription-medications${query ? `?${query}` : ""}`);
};
export const getPrescriptionMedication = async (id) => {
  const data = unwrapData(await catalogRequest(`/prescription-medications/${id}`));
  return data?.prescriptionMedication ?? data?.medication ?? data?.item ?? data;
};
export const createPrescriptionMedication = (payload) => catalogRequest("/prescription-medications", "POST", payload);
export const updatePrescriptionMedication = (id, payload) => catalogRequest(`/prescription-medications/${id}`, "PATCH", payload);
export const deletePrescriptionMedication = (id) => catalogRequest(`/prescription-medications/${id}`, "DELETE");

export const listOtcProducts = async (params = {}) => {
  const query = buildQuery(params);
  return catalogList(`/otc-products${query ? `?${query}` : ""}`);
};
export const getOtcProduct = async (id) => unwrapData(await catalogRequest(`/otc-products/${id}`));
const hasOtcProductFiles = (payload) => Array.from(payload.images || []).some((image) => image instanceof File);
const otcProductFormData = (payload) => {
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (key === "images") {
      Array.from(value).filter((image) => image instanceof File).forEach((image) => formData.append("images", image));
      return;
    }
    formData.append(key, value);
  });

  return formData;
};
const otcProductJsonPayload = (payload) => {
  const { images, ...jsonPayload } = payload;
  return jsonPayload;
};
export const createOtcProduct = (payload) => hasOtcProductFiles(payload)
  ? apiRequest("/otc-products", { method: "POST", body: otcProductFormData(payload) })
  : catalogRequest("/otc-products", "POST", otcProductJsonPayload(payload));
export const updateOtcProduct = (id, payload) => hasOtcProductFiles(payload)
  ? apiRequest(`/otc-products/${id}`, { method: "PATCH", body: otcProductFormData(payload) })
  : catalogRequest(`/otc-products/${id}`, "PATCH", otcProductJsonPayload(payload));
export const deleteOtcProduct = (id) => catalogRequest(`/otc-products/${id}`, "DELETE");

export const listBillings = async (params = {}) => {
  const query = buildQuery(params);
  return toList(await catalogRequest(`/billings${query ? `?${query}` : ""}`));
};
export const getBilling = async (id) => unwrapData(await catalogRequest(`/billings/${id}`));
export const createBilling = (payload) => catalogRequest("/billings", "POST", payload);
export const createBillingForPatient = (payload) => catalogRequest("/billings/for-patient", "POST", payload);
export const updateBillingStatus = (id, payload) => catalogRequest(`/billings/${id}/status`, "PATCH", payload);
export const deleteBilling = (id) => catalogRequest(`/billings/${id}`, "DELETE");

export const listMedicines = async (params) => {
  const query = buildQuery(params);
  const response = await apiRequest(`/medicines${query ? `?${query}` : ""}`);

  return toList(response);
};

const appendMedicineForm = (payload) => {
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;

    if (key === "images") {
      Array.from(value || []).forEach((file) => formData.append("images", file));
      return;
    }

    if (key === "imageUrls") {
      value
        .filter(Boolean)
        .forEach((imageUrl) => formData.append("imageUrls", imageUrl));
      return;
    }

    formData.append(key, value);
  });

  return formData;
};

const hasMedicineFiles = (payload) => Array.from(payload.images || []).length > 0;

const buildMedicineJsonPayload = (payload) => {
  const { images, ...jsonPayload } = payload;

  return Object.entries(jsonPayload).reduce((nextPayload, [key, value]) => {
    if (value !== undefined && value !== "") {
      nextPayload[key] = value;
    }

    return nextPayload;
  }, {});
};

export const createMedicine = async (payload) => {
  if (!hasMedicineFiles(payload)) {
    return apiRequest("/medicines", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(buildMedicineJsonPayload(payload)),
    });
  }

  return apiRequest("/medicines", {
    method: "POST",
    body: appendMedicineForm(payload),
  });
};

export const updateMedicine = async (id, payload) => {
  if (!hasMedicineFiles(payload)) {
    return apiRequest(`/medicines/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(buildMedicineJsonPayload(payload)),
    });
  }

  return apiRequest(`/medicines/${id}`, {
    method: "PATCH",
    body: appendMedicineForm(payload),
  });
};

export const deleteMedicine = async (id) => {
  return apiRequest(`/medicines/${id}`, { method: "DELETE" });
};

export const listCategories = async (params) => {
  const query = buildQuery(params);
  const response = await apiRequest(`/categories${query ? `?${query}` : ""}`);

  return toList(response);
};

const categoryRequest = (path, method, payload) =>
  apiRequest(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: String(payload.name || "").trim(),
      description: String(payload.description || "").trim(),
      isActive: Boolean(payload.isActive),
    }),
  });

export const createCategory = async (payload) => {
  return categoryRequest("/categories", "POST", payload);
};

export const updateCategory = async (id, payload) => {
  return categoryRequest(`/categories/${id}`, "PATCH", payload);
};

export const deleteCategory = async (id) => {
  return apiRequest(`/categories/${id}`, { method: "DELETE" });
};

export const listSubcategories = async (categoryId, params) => {
  const query = buildQuery(params);
  const response = await apiRequest(
    `/categories/${categoryId}/subcategories${query ? `?${query}` : ""}`,
  );

  return toList(response);
};

export const createSubcategory = async (categoryId, payload) => {
  return categoryRequest(`/categories/${categoryId}/subcategories`, "POST", payload);
};

export const updateSubcategory = async (categoryId, id, payload) => {
  void categoryId;

  return categoryRequest(`/categories/subcategories/${id}`, "PATCH", payload);
};

export const deleteSubcategory = async (categoryId, id) => {
  return apiRequest(`/categories/${categoryId}/subcategories/${id}`, {
    method: "DELETE",
  });
};

export const listOrders = async (params = {}) => {
  const query = buildQuery(params);
  const response = await apiRequest(`/orders${query ? `?${query}` : ""}`);
  const data = unwrapData(response);
  const items = toList(response);
  const pagination = data.pagination || response?.pagination || {};
  const total = Number(pagination.total ?? data.total ?? response?.total ?? items.length);
  const limit = Number(pagination.limit ?? data.limit ?? response?.limit ?? params.limit ?? items.length) || 1;

  items.pagination = {
    total,
    page: Number(pagination.page ?? data.page ?? response?.page ?? params.page ?? 1),
    limit,
    totalPages: Number(pagination.totalPages ?? data.totalPages ?? response?.totalPages ?? Math.max(1, Math.ceil(total / limit))),
  };
  return items;
};

export const getOrder = async (id) => {
  const data = unwrapData(await apiRequest(`/orders/${id}`));
  return data?.order ?? data?.item ?? data?.data ?? data;
};

export const createOrder = (payload) => apiRequest("/orders", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(payload),
});

export const createCounterOrder = (payload) => apiRequest("/orders/counter", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(payload),
});

export const updateOrderStatus = async (id, status, details = {}) => {
  return apiRequest(`/orders/${id}/status`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status, ...details }),
  });
};

export const shipOrder = async (id, driverId) =>
  apiRequest(`/orders/${encodeURIComponent(id)}/ship`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ driverId }),
  });

export const assignDeliveryDriver = async (orderId, driverId) =>
  apiRequest(`/driver-deliveries/orders/${encodeURIComponent(orderId)}/assign`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ driverId }),
  });

export const updateOrderDeliveryDate = async (id, deliveryDate) =>
  apiRequest(`/orders/${encodeURIComponent(id)}/delivery-date`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deliveryDate }),
  });

export const updatePaymentStatus = async (id, paymentStatus) => {
  return apiRequest(`/orders/${id}/payment-status`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ paymentStatus }),
  });
};

export const cancelOrder = async (id) => {
  return apiRequest(`/orders/${id}/cancel`, { method: "PATCH" });
};

export const deleteOrder = async (id) => {
  return apiRequest(`/orders/${id}`, { method: "DELETE" });
};

export const deleteKupatHolimRequest = async (id) => {
  return apiRequest(`/kupat-holim-requests/${id}`, { method: "DELETE" });
};

export const listDashboardRequestsOrders = async (params) => {
  const query = buildQuery(params);
  const response = await apiRequest(
    `/dashboard/requests-orders${query ? `?${query}` : ""}`,
  );
  const data = unwrapData(response);
  const items = toList(response);
  const pagination = data.pagination || response?.pagination || {};
  const total = Number(pagination.total ?? data.total ?? response?.total ?? items.length);
  const limit = Number(
    (pagination.limit ?? data.limit ?? response?.limit ?? params?.limit ?? items.length) || 1,
  );

  return {
    items,
    total,
    page: Number(pagination.page ?? data.page ?? response?.page ?? params?.page ?? 1),
    limit,
    totalPages: Number(
      pagination.totalPages ??
        data.totalPages ??
        response?.totalPages ??
        Math.max(1, Math.ceil(total / limit)),
    ),
    hasNextPage: Boolean(pagination.hasNextPage),
    hasPreviousPage: Boolean(pagination.hasPreviousPage),
    nextPage: pagination.nextPage ?? null,
    previousPage: pagination.previousPage ?? null,
  };
};

export const listDashboardWorkQueue = async (params = {}) => {
  const query = buildQuery(params);
  const response = await apiRequest(`/dashboard/work-queue${query ? `?${query}` : ""}`);
  const data = unwrapData(response);
  const items = toList(response);
  const pagination = data.pagination || response?.pagination || {};
  const total = Number(pagination.total ?? data.total ?? response?.total ?? items.length);
  const limit = Number(pagination.limit ?? data.limit ?? response?.limit ?? params.limit ?? 20) || 20;
  return {
    items,
    total,
    page: Number(pagination.page ?? data.page ?? response?.page ?? params.page ?? 1),
    limit,
    totalPages: Number(pagination.totalPages ?? data.totalPages ?? response?.totalPages ?? Math.max(1, Math.ceil(total / limit))),
    hasNextPage: Boolean(pagination.hasNextPage),
    hasPreviousPage: Boolean(pagination.hasPreviousPage),
  };
};

export const getAddressById = async (id) => {
  const response = await apiRequest(`/addresses/${id}`);

  return unwrapData(response);
};

export const respondToQuotation = async (quotationId, payload) => {
  return apiRequest(`/quotations/${quotationId}/respond`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
};

export const closeQuotation = async (quotationId) => {
  return apiRequest(`/quotations/${quotationId}/close`, { method: "PATCH" });
};

export const cancelQuotation = async (quotationId) => {
  return apiRequest(`/quotations/${quotationId}/cancel`, { method: "PATCH" });
};
